import React, { useCallback, useEffect, useRef, useState } from "react";
import { Document, Page } from "react-pdf";
import "../../utils/pdfWorker";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";
import { ReactPdfGeometrie } from "./pdf-editor/reactPdfGeometrie";
import type { SeitenGeometrie } from "./pdf-editor/seitenGeometrie";
import { PdfIcons } from "./pdf-editor/pdfIcons";
import "./pdf-editor/pdfEditor.css";
import { ZOOM_START, zoomGroesser, zoomKleiner } from "./pdf-editor/zoom";

/**
 * Read-only-PDF-Viewer (Fundament der späteren PDF-App): alle Seiten unter-
 * einander, Zoom, optionales Suchwort-Highlight (<mark> im Textlayer) und
 * automatischer Sprung zur ersten Fundstellen-Seite.
 *
 * Zoom: Ohne `scale` führt der Viewer ihn selbst und zeigt eine kleine eigene
 * Leiste (Mail-Anhänge ohne Dokument-Id). Mit `scale` gehört der Zoom dem
 * Aufrufer — `PdfDocumentView` hat die Knöpfe in seiner eigenen Leiste.
 */

interface Props {
  url: string;
  searchTerm?: string;
  /**
   * Ebene über den Seiten (z. B. Kommentare). Bekommt die Geometrie der
   * gerenderten Seiten und den scrollenden Container.
   */
  ebene?: (ctx: { geometrie: SeitenGeometrie; container: HTMLElement | null }) => React.ReactNode;
  /** Zoom von außen (1 = 100 %). Dann zeigt der Viewer keine eigene Leiste. */
  scale?: number;
  /** Text, solange die Datei lädt — z. B. „Bild wird geladen…“. */
  ladeText?: string;
}

const escapeRegExp = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

// customTextRenderer geht bei react-pdf durch innerHTML (sanitisiert, aber
// img/style/a bleiben erlaubt) — PDF-Text KANN `<`/`&` enthalten und würde
// sonst den Textlayer zerbrechen oder ungewollt HTML einschleusen. Deshalb vor
// dem Highlight escapen.
const escapeHtml = (s: string) =>
  s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");

export const PdfFileViewer: React.FC<Props> = ({ url, searchTerm, ebene, scale: scaleVorgabe, ladeText = "PDF wird geladen…" }) => {
  const [numPages, setNumPages] = useState(0);
  const [eigeneScale, setScale] = useState(ZOOM_START);
  const scale = scaleVorgabe ?? eigeneScale;
  // Geometrie für Ebenen über den Seiten — eine Instanz je Viewer, kein State:
  // Registrieren darf kein Neurendern auslösen.
  const [geometrie] = useState(() => new ReactPdfGeometrie());
  const [scrollContainer, setScrollContainer] = useState<HTMLDivElement | null>(null);
  useEffect(() => geometrie.setzeScale(scale), [geometrie, scale]);
  useEffect(() => geometrie.setzeAnzahl(numPages), [geometrie, numPages]);
  const pageRefs = useRef<Record<number, HTMLDivElement | null>>({});
  const jumped = useRef(false);
  const [targetPage, setTargetPage] = useState<number | null>(null);
  const needle = (searchTerm ?? "").trim();

  const onLoad = useCallback(
    async (pdf: { numPages: number; getPage: (n: number) => Promise<any> }) => {
      setNumPages(pdf.numPages);
      if (!needle) return;
      // Erste Seite mit Fundstelle suchen (Textlayer-Inhalt, case-insensitiv).
      const lower = needle.toLowerCase();
      for (let n = 1; n <= pdf.numPages; n++) {
        const page = await pdf.getPage(n);
        const content = await page.getTextContent();
        const text = content.items.map((it: { str?: string }) => it.str ?? "").join(" ");
        if (text.toLowerCase().includes(lower)) {
          setTargetPage(n);
          return;
        }
      }
    },
    [needle],
  );

  // Zur Fundstellen-Seite scrollen, sobald sie gerendert ist.
  useEffect(() => {
    if (targetPage == null || jumped.current) return;
    const el = pageRefs.current[targetPage];
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
      jumped.current = true;
    }
  });

  const highlight = useCallback(
    ({ str }: { str: string }) => {
      // Text zuerst escapen, dann gegen den ebenfalls escapten Suchbegriff matchen
      // (sonst würde z.B. "&" im Treffer nicht mehr zum bereits escapten "&amp;" passen).
      const safe = escapeHtml(str);
      if (!needle) return safe;
      return safe.replace(
        new RegExp(escapeRegExp(escapeHtml(needle)), "gi"),
        (m) => `<mark>${m}</mark>`,
      );
    },
    [needle],
  );

  return (
    <div className="pdfx pdfx-lesen">
      {scaleVorgabe === undefined && (
        <div className="pdfx-lesen-leiste" role="toolbar" aria-label="Ansicht">
          <button type="button" className="pdfx-wk kompakt" aria-label="Verkleinern" title="Verkleinern" onClick={() => setScale(zoomKleiner)}>
            {PdfIcons.zoomMinus}
          </button>
          <span className="pdfx-zoomwert">{Math.round(scale * 100)} %</span>
          <button type="button" className="pdfx-wk kompakt" aria-label="Vergrößern" title="Vergrößern" onClick={() => setScale(zoomGroesser)}>
            {PdfIcons.zoomPlus}
          </button>
          {needle && <span className="pdfx-suche">Suche: „{needle}"</span>}
        </div>
      )}
      <div ref={setScrollContainer} className="pdfx-lesen-flaeche">
        <Document file={url} onLoadSuccess={onLoad} loading={<div className="pdfx-lesen-status">{ladeText}</div>}>
          {Array.from({ length: numPages }, (_, i) => (
            <div
              key={i + 1}
              ref={(el) => { pageRefs.current[i + 1] = el; }}
              style={{ display: "flex", justifyContent: "center", marginBottom: 12 }}
            >
              {/* Eigener, positionierter Rahmen je Seite: hier hängen Ebenen (Kommentare)
                  ihre Markierungen ein — nicht in react-pdfs eigenes Element. */}
              <div ref={(el) => geometrie.setzeElement(i, el)} style={{ position: "relative" }}>
                {/* Renderer nur setzen, wenn tatsächlich gesucht wird — ohne Suchbegriff
                    bleibt der native (sichere) Textlayer-Pfad von react-pdf unberührt. */}
                <Page
                  pageNumber={i + 1}
                  scale={scale}
                  customTextRenderer={needle ? highlight : undefined}
                  onLoadSuccess={(seite) => geometrie.setzeSeite(i, seite)}
                  onRenderSuccess={() => geometrie.gerendert()}
                />
              </div>
            </div>
          ))}
        </Document>
        {ebene?.({ geometrie, container: scrollContainer })}
      </div>
    </div>
  );
};
